import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { TranslationReplacementAction } from '@course-authoring/shared-types';
import type { AutosaveStatus } from './useAutosave';
import {
  DEFAULT_AUTOSAVE_INTERVAL_MS,
  useAutosaveInterval,
  useScopeAwareAutosaveLifecycle,
} from './useScopeAwareAutosaveLifecycle';

interface ScopedMetadataState<TDraft, TPatch, TProjection> {
  draft?: TDraft;
  baseline?: TDraft;
  replacementAction?: TranslationReplacementAction;
  /**
   * A decision whose decision-only commit failed. Unlike a staged
   * `replacementAction` (which must wait for a source change to carry it),
   * this one already earned its commit — later autosaves and flushes retry it
   * even when the source patch is empty, so navigation cannot discard it.
   */
  pendingDecisionRetry?: TranslationReplacementAction;
  save?: (patch: TPatch) => Promise<TProjection>;
  onSaved?: (projection: TProjection, patch: TPatch) => void;
  buildPatch?: (baseline: TDraft, current: TDraft) => TPatch;
  hasSourceChanges?: (patch: TPatch) => boolean;
  buildDecisionPatch?: (
    baseline: TDraft,
    current: TDraft,
    action: TranslationReplacementAction,
  ) => TPatch;
  inFlight?: Promise<void>;
}

export interface UseScopedMetadataAutosaveOptions<TDraft, TPatch, TProjection> {
  enabled: boolean;
  scopeKey: string;
  initialProjection: TProjection | undefined;
  projectionRevisionKey: string;
  replacementAction?: TranslationReplacementAction;
  save: (patch: TPatch) => Promise<TProjection>;
  onSaved?: (projection: TProjection, patch: TPatch) => void;
  intervalMs?: number;
  emptyDraft: TDraft;
  draftFromProjection: (projection: TProjection) => TDraft;
  buildPatch: (baseline: TDraft, current: TDraft) => TPatch;
  hasSourceChanges: (patch: TPatch) => boolean;
  mergeNewerDraft: (server: TDraft, submitted: TDraft, current: TDraft) => TDraft;
  buildDecisionPatch: (
    baseline: TDraft,
    current: TDraft,
    action: TranslationReplacementAction,
  ) => TPatch;
}

export interface UseScopedMetadataAutosaveResult<TDraft> {
  draft: TDraft;
  setDraft: Dispatch<SetStateAction<TDraft>>;
  status: AutosaveStatus;
  flushIfDirty: () => Promise<void>;
  saveDecision: (action: TranslationReplacementAction) => Promise<void>;
  hasPendingSourceChanges: () => boolean;
  /**
   * Synchronously supersedes the staged replacement decision for the active
   * scope. The `replacementAction` option only syncs on the next render, which
   * is too late for a flush racing an answer click — a superseded answer must
   * never ride a later save.
   */
  stageReplacementAction: (action: TranslationReplacementAction | undefined) => void;
}

export function useScopedMetadataAutosave<
  TDraft,
  TPatch extends { replacementAction?: TranslationReplacementAction },
  TProjection,
>({
  enabled,
  scopeKey,
  initialProjection,
  projectionRevisionKey,
  replacementAction,
  save,
  onSaved,
  intervalMs = DEFAULT_AUTOSAVE_INTERVAL_MS,
  emptyDraft,
  draftFromProjection,
  buildPatch,
  hasSourceChanges,
  mergeNewerDraft,
  buildDecisionPatch,
}: UseScopedMetadataAutosaveOptions<TDraft, TPatch, TProjection>): UseScopedMetadataAutosaveResult<TDraft> {
  const [draft, setDraft] = useState<TDraft>(emptyDraft);
  const statesRef = useRef(new Map<string, ScopedMetadataState<TDraft, TPatch, TProjection>>());
  const initialProjectionRef = useRef(initialProjection);
  initialProjectionRef.current = initialProjection;
  const {
    status,
    isCurrentScope,
    beginSave,
    markSaved,
    markFailed,
    resetFeedback,
  } = useScopeAwareAutosaveLifecycle(scopeKey);

  useEffect(() => {
    const state = getOrCreateState(statesRef.current, scopeKey);
    state.replacementAction = replacementAction;
    state.save = save;
    state.onSaved = onSaved;
    state.buildPatch = buildPatch;
    state.hasSourceChanges = hasSourceChanges;
    state.buildDecisionPatch = buildDecisionPatch;
  }, [
    buildDecisionPatch,
    buildPatch,
    hasSourceChanges,
    onSaved,
    replacementAction,
    save,
    scopeKey,
  ]);

  useEffect(() => {
    const projection = initialProjectionRef.current;
    if (!projection) return;
    const state = getOrCreateState(statesRef.current, scopeKey);
    const next = draftFromProjection(projection);
    const merged = state.baseline && state.draft
      ? mergeNewerDraft(next, state.baseline, state.draft)
      : next;
    state.baseline = next;
    state.draft = merged;
    setDraft(merged);
    resetFeedback();
  }, [draftFromProjection, mergeNewerDraft, projectionRevisionKey, resetFeedback, scopeKey]);

  const applySaved = useCallback((
    targetScopeKey: string,
    projection: TProjection,
    patch: TPatch,
    submitted: TDraft,
  ) => {
    const state = getOrCreateState(statesRef.current, targetScopeKey);
    // A persisted decision consumes the staged copy immediately so a
    // concurrent persist cannot re-send it before the caller's state updates.
    if (patch.replacementAction) {
      state.replacementAction = undefined;
      state.pendingDecisionRetry = undefined;
    }
    const server = draftFromProjection(projection);
    const merged = mergeNewerDraft(server, submitted, state.draft ?? submitted);
    state.baseline = server;
    state.draft = merged;
    if (!isCurrentScope(targetScopeKey)) return;
    setDraft(merged);
    state.onSaved?.(projection, patch);
    markSaved(targetScopeKey);
  }, [draftFromProjection, isCurrentScope, markSaved, mergeNewerDraft]);

  const persist = useCallback(async function persistScope(
    targetScopeKey: string,
    decision?: TranslationReplacementAction,
  ): Promise<void> {
    const state = getOrCreateState(statesRef.current, targetScopeKey);
    if (state.inFlight) {
      await state.inFlight;
      return persistScope(targetScopeKey, decision);
    }
    if (
      !state.baseline
      || !state.buildPatch
      || !state.hasSourceChanges
      || !state.buildDecisionPatch
    ) return;
    const current = state.draft ?? state.baseline;
    const patch = decision
      ? state.buildDecisionPatch(state.baseline, current, decision)
      : state.buildPatch(state.baseline, current);
    if (!decision && state.hasSourceChanges(patch) && state.replacementAction) {
      patch.replacementAction = state.replacementAction;
    }
    if (!decision && !patch.replacementAction && state.pendingDecisionRetry) {
      patch.replacementAction = state.pendingDecisionRetry;
    }
    if (Object.keys(patch).length === 0 || !state.save) return;
    if (isCurrentScope(targetScopeKey)) beginSave();
    let promise: Promise<void>;
    promise = state.save(patch)
      .then((projection) => applySaved(targetScopeKey, projection, patch, current))
      .catch((error) => {
        // A failed decision-only commit has no source change left to carry a
        // retry — remember it so the next autosave or flush re-sends it.
        if (patch.replacementAction && !state.hasSourceChanges?.(patch)) {
          state.pendingDecisionRetry = patch.replacementAction;
        }
        markFailed(targetScopeKey, error, 'Failed to save metadata changes.');
        throw error;
      })
      .finally(() => {
        if (state.inFlight === promise) state.inFlight = undefined;
      });
    state.inFlight = promise;
    return promise;
  }, [applySaved, beginSave, isCurrentScope, markFailed]);

  useAutosaveInterval(enabled && Boolean(initialProjection), intervalMs, () => {
    void persist(scopeKey).catch(() => undefined);
  });

  useEffect(() => () => {
    if (enabled) void persist(scopeKey).catch(() => undefined);
  }, [enabled, persist, scopeKey]);

  const setScopedDraft = useCallback<Dispatch<SetStateAction<TDraft>>>((update) => {
    const state = getOrCreateState(statesRef.current, scopeKey);
    const current = state.draft ?? state.baseline ?? draft;
    const next = typeof update === 'function'
      ? (update as (current: TDraft) => TDraft)(current)
      : update;
    state.draft = next;
    setDraft(next);
  }, [draft, scopeKey]);

  return {
    draft,
    setDraft: setScopedDraft,
    status,
    flushIfDirty: useCallback(() => persist(scopeKey), [persist, scopeKey]),
    saveDecision: useCallback(
      (action: TranslationReplacementAction) => persist(scopeKey, action),
      [persist, scopeKey],
    ),
    hasPendingSourceChanges: useCallback(() => {
      const state = statesRef.current.get(scopeKey);
      if (!state?.baseline || !state.buildPatch || !state.hasSourceChanges) return false;
      return state.hasSourceChanges(state.buildPatch(state.baseline, state.draft ?? state.baseline));
    }, [scopeKey]),
    stageReplacementAction: useCallback((action: TranslationReplacementAction | undefined) => {
      const state = getOrCreateState(statesRef.current, scopeKey);
      state.replacementAction = action;
      // A new answer supersedes a failed earlier commit; if this answer's own
      // commit fails, the save path re-marks the retry with the new value.
      state.pendingDecisionRetry = undefined;
    }, [scopeKey]),
  };
}

function getOrCreateState<TDraft, TPatch, TProjection>(
  states: Map<string, ScopedMetadataState<TDraft, TPatch, TProjection>>,
  scopeKey: string,
): ScopedMetadataState<TDraft, TPatch, TProjection> {
  const existing = states.get(scopeKey);
  if (existing) return existing;
  const created: ScopedMetadataState<TDraft, TPatch, TProjection> = {};
  states.set(scopeKey, created);
  return created;
}
